import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Game, Player } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

const Admin: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGame();
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}`);
      const data = await res.json();
      setGame(data);
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  };

  const revealMol = async (playerId: string) => {
    if (!game) return;
    try {
      await fetch(`${API_URL}/api/games/${game.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          molId: playerId,
          status: 'finished'
        })
      });
      setShowRevealModal(false);
      fetchGame();
    } catch (error) {
      console.error('Error revealing mol:', error);
    }
  };

  const deleteGame = async () => {
    if (!game) return;
    try {
      await fetch(`${API_URL}/api/games/${game.id}`, {
        method: 'DELETE'
      });
      navigate('/');
    } catch (error) {
      console.error('Error deleting game:', error);
    }
  };

  const resetMol = async () => {
    if (!game) return;
    try {
      await fetch(`${API_URL}/api/games/${game.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          molId: null,
          status: 'active'
        })
      });
      fetchGame();
    } catch (error) {
      console.error('Error resetting mol:', error);
    }
  };

  const getMolName = () => {
    if (!game || !game.molId) return null;
    const mol = game.players.find(p => p.id === game.molId);
    return mol?.name;
  };

  if (!game) {
    return <div className="page loading">Laden...</div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Admin</h1>
        <p>{game.name}</p>
      </div>

      {/* Current Status */}
      <div className="card">
        <h2>Spel Status</h2>
        <div style={{ marginTop: '15px' }}>
          <p><strong>Status:</strong> {game.status}</p>
          <p><strong>Kandidaten:</strong> {game.players.length}</p>
          <p><strong>Afleveringen:</strong> {game.episodes.length}</p>
          <p><strong>Stemmen:</strong> {game.votes.length}</p>
          {game.molId && (
            <p><strong>Mol:</strong> {getMolName()}</p>
          )}
        </div>
      </div>

      {/* Reveal Mol */}
      <div className="card">
        <h2>De Mol Onthullen</h2>
        <p style={{ color: 'var(--mol-text-muted)', marginBottom: '15px' }}>
          Onthul wie de mol is om de punten te berekenen.
        </p>
        
        {game.molId ? (
          <div>
            <p style={{ color: 'var(--mol-accent)', fontWeight: 'bold', marginBottom: '15px' }}>
              De mol is: {getMolName()}
            </p>
            <button className="btn btn-secondary" onClick={resetMol}>
              Reset Mol
            </button>
          </div>
        ) : (
          <button 
            className="btn btn-accent"
            onClick={() => setShowRevealModal(true)}
          >
            Onthul de Mol
          </button>
        )}
      </div>

      {/* Danger Zone */}
      <div className="card" style={{ borderColor: 'var(--mol-red)', borderWidth: '2px', borderStyle: 'solid' }}>
        <h2 style={{ color: 'var(--mol-red)' }}>Danger Zone</h2>
        <p style={{ color: 'var(--mol-text-muted)', marginBottom: '15px' }}>
          Deze acties kunnen niet ongedaan gemaakt worden!
        </p>
        <button 
          className="btn btn-danger"
          onClick={() => setShowDeleteModal(true)}
        >
          Verwijder Spel
        </button>
      </div>

      <button 
        className="btn btn-secondary btn-block" 
        onClick={() => navigate(`/game/${gameId}`)}
        style={{ marginTop: '20px' }}
      >
        ← Terug naar spel
      </button>

      {/* Reveal Modal */}
      {showRevealModal && (
        <div className="modal-overlay" onClick={() => setShowRevealModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Wie is de Mol?</h2>
            <p style={{ color: 'var(--mol-text-muted)', marginBottom: '20px' }}>
              Selecteer de kandidaat die de mol is.
            </p>
            <div className="vote-grid">
              {game.players.map((player: Player) => (
                <div 
                  key={player.id}
                  className={`vote-card ${player.isEliminated ? 'eliminated' : ''}`}
                  onClick={() => !player.isEliminated && revealMol(player.id)}
                >
                  <div 
                    className="vote-avatar"
                    style={{ backgroundColor: player.color }}
                  >
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                  <div>{player.name}</div>
                  {player.isEliminated && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--mol-text-muted)' }}>
                      Afgevallen
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary btn-block"
                onClick={() => setShowRevealModal(false)}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: 'var(--mol-red)' }}>Spel Verwijderen?</h2>
            <p style={{ marginBottom: '20px' }}>
              Weet je zeker dat je "{game.name}" wilt verwijderen? 
              Alle stemmen en data gaan verloren.
            </p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuleren
              </button>
              <button 
                className="btn btn-danger"
                onClick={deleteGame}
              >
                Ja, Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
